
import Vue from 'vue'
import axios from 'axios'
import { Prop, Component } from 'vue-property-decorator'
import { Dispute, DisputeMessage } from '../../../global/models'
import { prettyDate } from '../../../global/functions/util'
import EmptyState from './EmptyState.vue'
import Message from './Message.vue'

@Component({
  components: { EmptyState, Message }
})
export default class DisputeMessages extends Vue {
  @Prop({ type: Object, required: true })
  private readonly dispute!: Dispute

  messages: DisputeMessage[] = []

  messageText: string | null = null

  sendingMessage = false

  get apiRoute() {
    return `/disputes/${this.dispute.id}/messages`
  }

  get canAddMessages() {
    return !['resolved', 'withdrawn', 'adjudicated'].includes(this.dispute.status)
  }

  get agent() {
    return this.$store.state.auth
  }

  get messagesContainer() {
    return this.$refs.messagesContainer as HTMLElement
  }

  async mounted() {
    const response = await axios.get<DisputeMessage[]>(this.apiRoute)
    this.messages = response.data
    this.scrollToBottom()
  }
  
  scrollToBottom() {
    this.$nextTick(() => {
      if (this.messagesContainer) {
        this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight
      }
    })
  }

  sameAuthorBelow(message: DisputeMessage) {
    const index = this.messages.findIndex(i => i.id === message.id)

    if (index + 1 === this.messages.length) {
      return false
    }

    return this.ownMessage(message) === this.ownMessage(this.messages[index + 1])
  }

  ownMessage(message: DisputeMessage) {
    return message.authorType === 'agency'
  }

  sameAuthorAbove(message: DisputeMessage) {
    const index = this.messages.findIndex(i => i.id === message.id)

    if (index - 1 < 0) {
      return false
    }

    return this.ownMessage(message) === this.ownMessage(this.messages[index - 1])
  }

  sameDateAbove(message: DisputeMessage) {
    const index = this.messages.findIndex(i => i.id === message.id)

    if (index - 1 < 0) {
      return false
    }

    return prettyDate(message.created) === prettyDate(this.messages[index - 1].created)
  }

  formatDate(value: string) {
    const date = new Date(value)
    return date.toLocaleString([], { hourCycle: 'h12' } as Intl.DateTimeFormatOptions)
  }

  submitOnEnter(event: KeyboardEvent) {
    if (event.keyCode === 13 && !event.shiftKey) {
      this.sendMessage()
    }
  }

  async sendMessage() {
    try {
      this.sendingMessage = true
      const response = await axios.post<DisputeMessage>(this.apiRoute, { text: this.messageText })
      this.messageText = null
      this.sendingMessage = false
      this.messages.push(response.data)
      this.scrollToBottom()
    } catch (error) {
      this.sendingMessage = false
      this.$store.dispatch('error', 'Error creating message')
    }
  }
}
